import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem("unstuck_admin_key") || "");
  const [input, setInput] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [orders, setOrders] = useState(null);
  const [trials, setTrials] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shippingId, setShippingId] = useState(null);

  const exportTrials = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/trials/export`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "unstuck-trial-facilities.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not export the trial list.");
    }
  };

  const removeItem = async (path, body) => {
    if (!window.confirm("Remove this record? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/admin/${path}`, body, { headers: { "X-Admin-Key": key } });
      toast.success("Record removed.");
      const headers = { "X-Admin-Key": key };
      const [trl, fb, ord] = await Promise.all([
        axios.get(`${API}/admin/trials`, { headers }),
        axios.get(`${API}/admin/feedback`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
      ]);
      setTrials(trl.data);
      setFeedback(fb.data.feedback);
      setOrders(ord.data.orders);
    } catch (e) {
      toast.error("Could not remove the record.");
    }
  };

  const markShipped = async (sessionId) => {
    setShippingId(sessionId);
    try {
      await axios.post(`${API}/admin/orders/${sessionId}/ship`, {}, { headers: { "X-Admin-Key": key } });
      toast.success("Shipping email sent to the customer.");
      const { data } = await axios.get(`${API}/admin/orders`, { headers: { "X-Admin-Key": key } });
      setOrders(data.orders);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the shipping email.");
    } finally {
      setShippingId(null);
    }
  };

  const load = async (adminKey) => {
    setLoading(true);
    setError(null);
    try {
      const headers = { "X-Admin-Key": adminKey };
      const [enq, ord, trl, fb] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/trials`, { headers }),
        axios.get(`${API}/admin/feedback`, { headers }),
      ]);
      setEnquiries(enq.data.enquiries);
      setOrders(ord.data.orders);
      setTrials(trl.data);
      setFeedback(fb.data.feedback);
      sessionStorage.setItem("unstuck_admin_key", adminKey);
      setKey(adminKey);
    } catch (e) {
      setError("Incorrect admin password.");
      sessionStorage.removeItem("unstuck_admin_key");
      setKey("");
    } finally {
      setLoading(false);
    }
  };

  const login = (e) => {
    e.preventDefault();
    load(input);
  };

  if (!key) {
    return (
      <div data-testid="admin-login" className="bg-cream">
        <div className="max-w-md mx-auto px-6 py-24">
          <form onSubmit={login} className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8">
            <div className="h-14 w-14 rounded-full bg-sage-light flex items-center justify-center mb-6">
              <Lock className="h-7 w-7 text-sage" aria-hidden="true" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-ink mb-2">Admin</h1>
            <p className="text-base text-ink/70 mb-6">Enter the admin password to view enquiries and orders.</p>
            <Label htmlFor="admin-password" className="text-base font-medium mb-1.5 block">Password</Label>
            <Input
              id="admin-password"
              type="password"
              data-testid="admin-password-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="bg-white border-2 border-border rounded-xl px-4 py-4 h-auto text-lg mb-4"
            />
            {error && <p data-testid="admin-error" className="text-destructive text-base mb-4">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="admin-login-button" className="w-full bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 h-auto text-lg font-medium">
              {loading && <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />}
              Sign In
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="bg-cream">
      <Seo title="Admin" noindex />
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        <h1 className="font-heading font-black text-4xl text-ink mb-10">Enquiries &amp; Orders</h1>
        <Tabs defaultValue="enquiries">
          <TabsList className="mb-8 flex-wrap h-auto">
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders ({orders?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="trials" data-testid="admin-tab-trials">Free Trials ({trials?.total ?? 0})</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback">Feedback ({feedback?.length ?? 0})</TabsTrigger>
          </TabsList>
          <TabsContent value="enquiries">
            {(!enquiries || enquiries.length === 0) && <p className="text-lg text-ink/70" data-testid="admin-no-enquiries">No enquiries yet.</p>}
            <div className="space-y-4">
              {enquiries?.map((enq, i) => (
                <div key={i} data-testid={`admin-enquiry-${i}`} className="bg-white rounded-2xl border border-border p-6">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className={`text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 ${enq.kind === "organization" ? "bg-gold-light text-gold-dark" : "bg-sage-light text-sage"}`}>
                      {enq.kind}
                    </span>
                    <span className="font-semibold text-ink text-lg">{enq.name}</span>
                    <a href={`mailto:${enq.email}`} className="text-sage hover:underline">{enq.email}</a>
                    {enq.organization && <span className="text-ink/70">• {enq.organization}</span>}
                    {enq.estimated_quantity && <span className="text-ink/70">• ~{enq.estimated_quantity} programs</span>}
                  </div>
                  <p className="text-base text-ink/80 whitespace-pre-wrap">{enq.message}</p>
                  <p className="text-sm text-ink/50 mt-3">{enq.created_at ? new Date(enq.created_at).toLocaleString() : ""}</p>
                  <button
                    type="button"
                    onClick={() => removeItem("enquiries/delete", { id: enq.id })}
                    data-testid={`admin-enquiry-remove-${i}`}
                    className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline underline-offset-4"
                  >
                    Remove this enquiry
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="orders">
            {(!orders || orders.length === 0) && <p className="text-lg text-ink/70" data-testid="admin-no-orders">No orders yet.</p>}
            <div className="space-y-4">
              {orders?.map((ord, i) => (
                <div key={i} data-testid={`admin-order-${i}`} className="bg-white rounded-2xl border border-border p-6 flex flex-wrap items-center gap-4">
                  <span className="font-semibold text-ink text-lg">
                    {ord.lookup_key === "seniors_program" ? "Complete Seniors Program" : ord.lookup_key === "extra_bucks" ? "Extra UNSTUCK Bucks (digital)" : ord.lookup_key === "seniors_deck" ? "Individual Seniors Deck" : `UNSTUCK deck (${ord.lookup_key})`}
                  </span>
                  <span className="text-ink/70">× {ord.quantity}</span>
                  <span className="text-ink/70">${((ord.amount || 0) / 100).toFixed(2)} {ord.currency?.toUpperCase()}</span>
                  <span className={`text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 ${ord.payment_status === "paid" ? "bg-sage-light text-sage" : "bg-gold-light text-gold-dark"}`}>
                    {ord.payment_status}
                  </span>
                  {ord.shipped_at && (
                    <span className="text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 bg-sage text-white" data-testid={`admin-shipped-badge-${i}`}>
                      Shipped
                    </span>
                  )}
                  {ord.customer_email && <span className="text-ink/70">{ord.customer_email}</span>}
                  <span className="text-sm text-ink/50">{ord.created_at ? new Date(ord.created_at).toLocaleString() : ""}</span>
                  {ord.payment_status === "paid" && ord.lookup_key !== "extra_bucks" && !ord.shipped_at && (
                    <button
                      type="button"
                      onClick={() => markShipped(ord.session_id)}
                      disabled={shippingId === ord.session_id}
                      data-testid={`admin-ship-button-${i}`}
                      className="ml-auto inline-flex items-center gap-2 bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
                    >
                      {shippingId === ord.session_id && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                      Send "Order Shipped" Email
                    </button>
                  )}
                  {(ord.payment_status !== "paid" || ord.session_id?.startsWith("cs_test_")) && (
                    <button
                      type="button"
                      onClick={() => removeItem("orders/delete", { session_id: ord.session_id })}
                      data-testid={`admin-order-remove-${i}`}
                      className="ml-auto text-sm font-medium text-red-700 hover:text-red-900 underline underline-offset-4"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="trials">
            <div className="flex items-center justify-between mb-6">
              <p className="text-lg text-ink/75" data-testid="admin-trials-summary">
                {trials ? `${trials.active} active of ${trials.total} total` : ""}
              </p>
              <button
                type="button"
                onClick={exportTrials}
                data-testid="admin-export-trials-button"
                className="inline-flex items-center gap-2 bg-sage text-white hover:bg-sage-dark rounded-full px-6 py-3 text-base font-medium transition-colors"
              >
                Export Facility Contacts (CSV)
              </button>
            </div>
            {(!trials || trials.trials.length === 0) && <p className="text-lg text-ink/70" data-testid="admin-no-trials">No trial registrations yet.</p>}
            <div className="space-y-4">
              {trials?.trials.map((t, i) => (
                <div key={i} data-testid={`admin-trial-${i}`} className="bg-white rounded-2xl border border-border p-6">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="font-semibold text-ink text-lg">{t.facility}</span>
                    <span className={`text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 ${t.expired ? "bg-cream-muted text-ink/60" : "bg-sage-light text-sage"}`}>
                      {t.expired ? "Expired" : "Active"}
                    </span>
                    {t.purchase_interest && (
                      <span className="text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 bg-gold-light text-gold-dark">
                        Interested in purchasing
                      </span>
                    )}
                  </div>
                  <p className="text-base text-ink/80">
                    {t.name} — {t.job_title} • {t.community_label} •{" "}
                    <a href={`mailto:${t.email}`} className="text-sage hover:underline">{t.email}</a>
                    {t.phone ? ` • ${t.phone}` : ""}
                  </p>
                  <p className="text-sm text-ink/60 mt-2">
                    Registered {t.created_at?.slice(0, 10)} • Expires {t.expires_at?.slice(0, 10)} •
                    Emails sent: {(t.emails_sent || []).join(", ") || "welcome"} •
                    Email opt-in: {t.email_opt_in ? "Yes" : "No"}
                  </p>
                  <button
                    type="button"
                    onClick={() => removeItem("trials/delete", { token: t.token })}
                    data-testid={`admin-trial-remove-${i}`}
                    className="mt-3 text-sm font-medium text-red-700 hover:text-red-900 underline underline-offset-4"
                  >
                    Remove this registration
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="feedback">
            {(!feedback || feedback.length === 0) && <p className="text-lg text-ink/70" data-testid="admin-no-feedback">No feedback yet.</p>}
            <div className="space-y-4">
              {feedback?.map((f, i) => (
                <div key={i} data-testid={`admin-feedback-${i}`} className="bg-white rounded-2xl border border-border p-6">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="font-semibold text-ink text-lg">{f.facility || "General feedback"}</span>
                    {f.contact_name && <span className="text-ink/70">{f.contact_name}</span>}
                    {f.publish_permission && (
                      <span className="text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 bg-sage-light text-sage">May publish</span>
                    )}
                    {f.interested_in_purchase && (
                      <span className="text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1 bg-gold-light text-gold-dark">Interested in purchasing</span>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-base text-ink/80 mb-3">
                    <p><span className="text-ink/60">Easy for staff:</span> {f.staff_ease}</p>
                    <p><span className="text-ink/60">Fit different abilities:</span> {f.abilities_fit}</p>
                    <p><span className="text-ink/60">Would recommend:</span> {f.would_recommend}</p>
                  </div>
                  <p className="text-base text-ink/80"><span className="text-ink/60">Residents' response:</span> {f.resident_response}</p>
                  {f.testimonial && (
                    <p className="mt-3 bg-cream rounded-xl p-4 text-base italic text-ink/85">"{f.testimonial}"</p>
                  )}
                  <p className="text-sm text-ink/50 mt-3">{f.created_at ? new Date(f.created_at).toLocaleString() : ""}</p>
                  <button
                    type="button"
                    onClick={() => removeItem("feedback/delete", { id: f.id })}
                    data-testid={`admin-feedback-remove-${i}`}
                    className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline underline-offset-4"
                  >
                    Remove this feedback
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
